import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePeopleSearch, useUpdateStatus } from '../../hooks/useMarketing';

const STATUS_LABELS = {
  not_contacted: 'Awaiting report',
  first_contact: 'First contact (legacy)',
  report_sent: 'Report sent',
  checkout_nudge: 'First nudge',
  second_contact: 'Second nudge',
  call: 'Call list',
  feedback: 'Feedback',
  responded: 'Responded',
  paid: 'Paid',
  do_not_contact: 'Do not contact',
  not_serious: 'Not serious',
};

function Search() {
  const navigate = useNavigate();
  const [input, setInput] = useState('');
  const [query, setQuery] = useState('');
  const updateStatus = useUpdateStatus();

  // Debounce typing → query
  useEffect(() => {
    const t = setTimeout(() => setQuery(input.trim()), 300);
    return () => clearTimeout(t);
  }, [input]);

  const { data, isFetching } = usePeopleSearch(query);
  const results = data?.results || [];

  const markStatus = (person, status, label) => {
    if (!window.confirm(`Mark ${person.name || person.email} as ${label}?`)) return;
    updateStatus.mutate({ visitorId: person.id, status });
  };

  return (
    <div className="mkt-feedback">
      <div className="mkt-funnel-header">
        <h2>Search People</h2>
      </div>

      <input
        type="text"
        autoFocus
        className="mkt-search-input"
        placeholder="Search by name, email, phone, trademark or business…"
        value={input}
        onChange={(e) => setInput(e.target.value)}
      />

      {query.length >= 2 && (
        <p className="mkt-feedback-note">
          {isFetching ? 'Searching…' : `${results.length} ${results.length === 1 ? 'match' : 'matches'}${results.length === 25 ? ' (showing first 25)' : ''}`}
        </p>
      )}

      {query.length >= 2 && !isFetching && results.length === 0 && (
        <div className="mkt-feedback-empty">
          <p>No one matches &ldquo;{query}&rdquo;.</p>
          <p className="mkt-feedback-empty-hint">Searches name, email, phone, trademark and business name.</p>
        </div>
      )}

      <div className="mkt-feedback-list">
        {results.map((p) => (
          <div
            key={p.id}
            className="mkt-feedback-row"
            onClick={() => navigate(`/marketing/visitor/${p.id}`)}
          >
            <div className="mkt-feedback-row-main">
              <div className="mkt-feedback-row-top">
                <span className="mkt-feedback-who">{p.name || p.email || 'Unknown'}</span>
                {p.email && p.name && <span className="mkt-feedback-email">{p.email}</span>}
                {p.trademarkName && <span className="mkt-visitor-tm">&ldquo;{p.trademarkName}&rdquo;</span>}
                <span className={`mkt-badge ${p.contactStatus === 'do_not_contact' ? 'mkt-badge-feedback-asked' : 'mkt-badge-returning'}`}>
                  {STATUS_LABELS[p.contactStatus] || p.contactStatus}
                </span>
                {p.paid && <span className="mkt-badge mkt-badge-feedback-given">Paid</span>}
              </div>
              <div className="mkt-feedback-row-meta">
                {p.businessName && <span>{p.businessName}</span>}
                {p.phone && (
                  <a href={`tel:${p.phone}`} onClick={(e) => e.stopPropagation()} style={{ color: '#0f766e' }}>
                    · 📞 {p.phone}
                  </a>
                )}
                {p.currentStep && <span>· step: {p.currentStep}</span>}
                {p.cartValue && <span>· ${Number(p.cartValue).toLocaleString()} cart</span>}
                {p.feedbackReason && <span>· 💬 {p.feedbackReason.replace('_', ' ')}</span>}
              </div>
            </div>
            <div className="mkt-search-actions" onClick={(e) => e.stopPropagation()}>
              {p.contactStatus !== 'do_not_contact' && (
                <button
                  className="mkt-card-quick-btn mkt-quick-dnc"
                  onClick={() => markStatus(p, 'do_not_contact', 'Do Not Contact')}
                  title="They asked not to be messaged"
                >
                  ⛔ DNC
                </button>
              )}
              {!['not_serious', 'do_not_contact', 'paid'].includes(p.contactStatus) && (
                <button
                  className="mkt-card-quick-btn mkt-quick-not-serious"
                  onClick={() => markStatus(p, 'not_serious', 'Not Serious')}
                >
                  🚫 Not serious
                </button>
              )}
              <span className="mkt-feedback-row-arrow">→</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default Search;
